import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';

interface GapIndicator {
  skill: string;
  requiredBy: string[] | string;
  requiredCount: number;
  availableCount: number;
  gap: number;
  priority: 'high' | 'medium' | 'low';
  recommendation: string;
}

interface GapIndicatorsSectionProps {
  data: GapIndicator[];
  expanded?: boolean;
  onViewMore?: () => void;
}

const GapIndicatorsSection: React.FC<GapIndicatorsSectionProps> = ({ data, expanded = false, onViewMore }) => {
  const { t } = useTranslation();

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'low':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-border';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'AlertCircle';
      case 'medium':
        return 'AlertTriangle';
      case 'low':
        return 'Info';
      default:
        return 'Info';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Icon name="AlertTriangle" size={24} className="mr-2 text-orange-600" />
            {t('Gap Indicators')}
          </h2>
          <p className="text-sm text-gray-600 mt-1">{t('Skills required by roles vs skills available')}</p>
        </div>
        <span className="text-sm text-gray-500">{data?.length} {t('gaps identified')}</span>
      </div>

      <div className="space-y-4">
        {data?.slice(0, expanded ? data?.length : 3)?.map((gap, index) => (
          <div
            key={index}
            className="border border-border rounded-lg p-4 hover:border-primary/50 hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-base font-semibold text-gray-900">{gap?.skill}</h3>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(gap?.priority)}`}>
                    <Icon name={getPriorityIcon(gap?.priority) as any} size={12} className="mr-1" />
                    {gap?.priority?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span className="flex items-center">
                    <Icon name="Target" size={12} className="mr-1" />
                    {t('Required:')} {gap?.requiredCount}
                  </span>
                  <span className="flex items-center">
                    <Icon name="CheckCircle" size={12} className="mr-1" />
                    {t('Available:')} {gap?.availableCount}
                  </span>
                  <span className="flex items-center font-medium text-red-600">
                    <Icon name="AlertCircle" size={12} className="mr-1" />
                    {t('Gap:')} {gap?.gap}
                  </span>
                </div>
              </div>
            </div>

            {/* Gap Visualization */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                <span>{t('Coverage')}</span>
                <span>{Math.round((gap?.availableCount / gap?.requiredCount) * 100)}% {t('fulfilled')}</span>
              </div>
              <div className="flex h-6 rounded-lg overflow-hidden">
                <div
                  className="bg-green-500 flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${(gap?.availableCount / gap?.requiredCount) * 100}%` }}
                >
                  {gap?.availableCount > 0 && gap?.availableCount}
                </div>
                <div
                  className="bg-red-500 flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${(gap?.gap / gap?.requiredCount) * 100}%` }}
                >
                  {gap?.gap > 0 && gap?.gap}
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center space-x-4 text-xs">
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded mr-1" />
                    {t('Available')}
                  </span>
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-red-500 rounded mr-1" />
                    {t('Gap')}
                  </span>
                </div>
              </div>
            </div>

            {/* Required By */}
            <div className="mb-3">
              <p className="text-xs text-gray-600 mb-1">{t('Required by:')}</p>
              <div className="flex flex-wrap gap-2">
                {Array.isArray(gap?.requiredBy) ? (
                  gap?.requiredBy?.map((dept, deptIndex) => (
                    <span
                      key={deptIndex}
                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                    >
                      {dept}
                    </span>
                  ))
                ) : (
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                    {gap?.requiredBy}
                  </span>
                )}
              </div>
            </div>

            {/* Recommendation */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="Lightbulb" size={16} className="text-blue-600 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-blue-900 mb-1">{t('Recommendation')}</p>
                  <p className="text-xs text-blue-800">{gap?.recommendation}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!expanded && data?.length > 3 && (
        <div className="mt-4 text-center">
          {onViewMore ? (
            <button
              onClick={onViewMore}
              className="text-primary hover:text-primary/80 text-sm font-medium"
            >
              {t('View All')} {data?.length} {t('Gaps')}
            </button>
          ) : (
            <p className="text-sm text-gray-500">{t('Showing')} 3 {t('of')} {data?.length} {t('gaps')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default GapIndicatorsSection;
